import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Button,
  Chip,
  CircularProgress,
  Divider,
  Drawer,
  IconButton,
  MenuItem,
  Stack,
  TextField,
  Toolbar,
  Typography,
} from '@mui/material'
import AddIcon from '@mui/icons-material/Add'
import CloseIcon from '@mui/icons-material/Close'
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import { toast } from 'react-toastify'
import { useCallback, useEffect, useState } from 'react'
import {
  listQuantityUnits,
  saveIssuedDietPlan,
} from 'src/mentor/services/dietPlanService'
import {
  DIETARY_FLAG_OPTIONS,
  LANGUAGE_OPTIONS,
  languageLabel,
} from './dietPlanLabels'

/**
 * Full diet-plan editor. Loaded with either:
 *   - a `templateDraft` (newly picked template, snapshotted into editable shape), or
 *   - an `existingPlan` (an already-issued plan being amended).
 *
 * The editor mutates a local draft and ships it via {@link saveIssuedDietPlan} on save. The parent
 * supplies `bookedEventId` (and patient context for free-form notes); everything else is
 * server-stamped or comes from the draft.
 */
export default function DietPlanEditorDrawer({
  open,
  onClose,
  bookedEventId,
  patientName,
  templateDraft,
  existingPlan,
  defaultLanguage = 'en',
  onSaved,
}) {
  const [draft, setDraft] = useState(null)
  const [units, setUnits] = useState([])
  const [saving, setSaving] = useState(false)

  // Hydrate the editor draft once whenever a fresh template/plan is supplied.
  useEffect(() => {
    if (!open) return
    if (existingPlan) {
      setDraft(planFromExisting(existingPlan))
      return
    }
    if (templateDraft) {
      setDraft(planFromTemplate(templateDraft, defaultLanguage))
      return
    }
    // Defensive: reset so a stale draft never leaks across sessions.
    setDraft(null)
  }, [open, existingPlan, templateDraft, defaultLanguage])

  useEffect(() => {
    if (!open) return undefined
    let cancelled = false
    ;(async () => {
      try {
        const u = await listQuantityUnits()
        if (!cancelled) setUnits(u)
      } catch (err) {
        if (!cancelled) console.error('Failed to load quantity units', err)
      }
    })()
    return () => {
      cancelled = true
    }
  }, [open])

  const update = useCallback((patch) => {
    setDraft((prev) => ({ ...(prev ?? {}), ...patch }))
  }, [])

  const updateMeals = useCallback((mealsUpdater) => {
    setDraft((prev) => {
      if (!prev) return prev
      const meals = typeof mealsUpdater === 'function' ? mealsUpdater(prev.meals ?? []) : mealsUpdater
      return { ...prev, meals }
    })
  }, [])

  const handleSave = useCallback(async () => {
    if (!draft) return
    if (!bookedEventId) {
      toast.error('Cannot save — no booked event in context')
      return
    }
    setSaving(true)
    try {
      const payload = {
        ...draft,
        bookedEventId,
      }
      const { issuedDietPlan } = await saveIssuedDietPlan(payload)
      if (!issuedDietPlan) {
        toast.error('Save failed — server returned no plan')
        return
      }
      toast.success('Diet plan saved')
      onSaved?.(issuedDietPlan)
      onClose?.()
    } catch (err) {
      console.error('Failed to save diet plan', err)
      const msg = err?.response?.data?.message ?? err?.message ?? 'Save failed'
      toast.error(msg)
    } finally {
      setSaving(false)
    }
  }, [draft, bookedEventId, onSaved, onClose])

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={saving ? undefined : onClose}
      PaperProps={{ sx: { width: { xs: '100%', md: 880 }, maxWidth: '95vw' } }}
    >
      <Toolbar sx={{ px: 3, py: 2, borderBottom: '1px solid rgba(0,0,0,0.08)' }}>
        <Typography variant="h6" sx={{ flex: 1, fontWeight: 700 }}>
          {existingPlan ? 'Edit diet plan' : 'Customise diet plan'}
          {patientName && (
            <Typography component="span" variant="body2" sx={{ ml: 1.5, color: 'text.secondary' }}>
              for {patientName}
            </Typography>
          )}
        </Typography>
        <IconButton onClick={onClose} disabled={saving} aria-label="Close diet plan editor">
          <CloseIcon />
        </IconButton>
      </Toolbar>

      <Box sx={{ p: 3, flex: 1, overflow: 'auto' }}>
        {!draft ? (
          <Stack alignItems="center" spacing={1} sx={{ py: 6 }}>
            <CircularProgress size={28} />
            <Typography variant="body2" color="text.secondary">
              Preparing draft…
            </Typography>
          </Stack>
        ) : (
          <Stack spacing={3}>
            <PlanHeaderSection draft={draft} update={update} />
            <PatientContextSection draft={draft} update={update} />
            <MealsEditorSection draft={draft} updateMeals={updateMeals} units={units} />
            <GuidelinesSection draft={draft} update={update} />
          </Stack>
        )}
      </Box>

      <Box
        sx={{
          p: 2,
          borderTop: '1px solid rgba(0,0,0,0.08)',
          background: '#fafafa',
          display: 'flex',
          gap: 2,
          justifyContent: 'flex-end',
        }}
      >
        <Button onClick={onClose} disabled={saving} variant="outlined">
          Cancel
        </Button>
        <Button onClick={handleSave} disabled={saving || !draft} variant="contained">
          {saving ? 'Saving…' : existingPlan ? 'Save changes' : 'Issue plan'}
        </Button>
      </Box>
    </Drawer>
  )
}

// ---------- Sections ----------

function PlanHeaderSection({ draft, update }) {
  return (
    <Box>
      <SectionTitle>Plan details</SectionTitle>
      <Stack spacing={2} sx={{ mt: 1 }}>
        <TextField
          label="Title"
          value={draft.title ?? ''}
          onChange={(e) => update({ title: e.target.value })}
          fullWidth
          size="small"
        />
        <TextField
          label="Description"
          value={draft.description ?? ''}
          onChange={(e) => update({ description: e.target.value })}
          fullWidth
          size="small"
          multiline
          maxRows={3}
        />
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
          <TextField
            label="Approx. kcal / day"
            type="number"
            value={draft.approxCaloriesPerDay ?? ''}
            onChange={(e) =>
              update({ approxCaloriesPerDay: e.target.value === '' ? null : Number(e.target.value) })
            }
            size="small"
            sx={{ minWidth: 160 }}
          />
          <TextField
            label="Macro split (free text)"
            value={draft.approxMacroSplit ?? ''}
            onChange={(e) => update({ approxMacroSplit: e.target.value })}
            size="small"
            sx={{ flex: 1 }}
          />
        </Stack>
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
          <TextField
            select
            label="Issuing in language"
            value={draft.language ?? 'en'}
            onChange={(e) => update({ language: e.target.value })}
            size="small"
            sx={{ minWidth: 200 }}
          >
            {LANGUAGE_OPTIONS.map((l) => (
              <MenuItem key={l.code} value={l.code}>
                {l.label}
              </MenuItem>
            ))}
          </TextField>
          <TextField
            label="Duration (days)"
            type="number"
            value={draft.durationDays ?? ''}
            onChange={(e) =>
              update({ durationDays: e.target.value === '' ? null : Number(e.target.value) })
            }
            size="small"
            sx={{ minWidth: 160 }}
          />
          <TextField
            label="Review after"
            placeholder="e.g. 2 weeks"
            value={draft.reviewAfter ?? ''}
            onChange={(e) => update({ reviewAfter: e.target.value })}
            size="small"
            sx={{ flex: 1 }}
          />
        </Stack>
        {draft.dietPlanTemplateCode && (
          <Typography variant="caption" color="text.secondary">
            Based on template <code>{draft.dietPlanTemplateCode}</code> v{draft.dietPlanTemplateVersion}
            {draft.language && ` · issuing in ${languageLabel(draft.language)}`}
          </Typography>
        )}
      </Stack>
    </Box>
  )
}

function PatientContextSection({ draft, update }) {
  return (
    <Box>
      <SectionTitle>Patient context</SectionTitle>
      <Stack spacing={2} sx={{ mt: 1 }}>
        <TextField
          label="Condition / clinical notes"
          placeholder="e.g. Type 2 diabetes, HbA1c 7.8, on metformin 500 mg BID"
          value={draft.patientConditionContext ?? ''}
          onChange={(e) => update({ patientConditionContext: e.target.value })}
          fullWidth
          multiline
          maxRows={4}
          size="small"
        />
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
          <TextField
            select
            label="Dietary preference"
            value={draft.dietaryPreference ?? ''}
            onChange={(e) => update({ dietaryPreference: e.target.value })}
            size="small"
            sx={{ minWidth: 200 }}
          >
            <MenuItem value="">—</MenuItem>
            {DIETARY_FLAG_OPTIONS.map((f) => (
              <MenuItem key={f.code} value={f.code}>
                {f.label}
              </MenuItem>
            ))}
          </TextField>
          <FreeFormChips
            label="Allergies / intolerances"
            values={draft.allergies ?? []}
            onChange={(next) => update({ allergies: next })}
          />
        </Stack>
        <TextField
          label="Practitioner notes (private to printout)"
          value={draft.practitionerNotes ?? ''}
          onChange={(e) => update({ practitionerNotes: e.target.value })}
          fullWidth
          multiline
          maxRows={4}
          size="small"
        />
      </Stack>
    </Box>
  )
}

function MealsEditorSection({ draft, updateMeals, units }) {
  const meals = draft.meals ?? []
  return (
    <Box>
      <Stack direction="row" alignItems="center" justifyContent="space-between">
        <SectionTitle>Meals ({meals.length})</SectionTitle>
        <Button
          size="small"
          startIcon={<AddIcon />}
          onClick={() => updateMeals((prev) => [...prev, blankSlot(prev.length)])}
        >
          Add meal slot
        </Button>
      </Stack>
      <Stack spacing={1.5} sx={{ mt: 1 }}>
        {meals.map((meal, mealIdx) => (
          <MealSlotEditor
            key={`meal-${mealIdx}`}
            meal={meal}
            mealIdx={mealIdx}
            units={units}
            onChange={(patch) =>
              updateMeals((prev) =>
                prev.map((m, i) => (i === mealIdx ? { ...m, ...patch } : m)),
              )
            }
            onRemove={() => updateMeals((prev) => prev.filter((_, i) => i !== mealIdx))}
          />
        ))}
        {meals.length === 0 && (
          <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
            No meals yet. Add a slot to start.
          </Typography>
        )}
      </Stack>
    </Box>
  )
}

function MealSlotEditor({ meal, mealIdx, units, onChange, onRemove }) {
  const options = meal.options ?? []
  const updateOptions = (updater) => {
    const next = typeof updater === 'function' ? updater(options) : updater
    onChange({ options: next })
  }
  return (
    <Accordion defaultExpanded={mealIdx < 2} variant="outlined" sx={{ borderRadius: 1.5 }}>
      <AccordionSummary expandIcon={<ExpandMoreIcon />}>
        <Stack direction="row" spacing={1} alignItems="center" sx={{ width: '100%' }}>
          <Typography sx={{ fontWeight: 600 }}>
            {meal.title || 'Meal slot'}
          </Typography>
          {meal.suggestedTime && (
            <Chip size="small" label={meal.suggestedTime} variant="outlined" />
          )}
          {meal.approxCalories != null && (
            <Chip size="small" label={`${meal.approxCalories} kcal`} variant="outlined" />
          )}
          <Box sx={{ flex: 1 }} />
          <IconButton
            size="small"
            onClick={(e) => {
              e.stopPropagation()
              onRemove?.()
            }}
            aria-label="Remove meal slot"
          >
            <DeleteOutlineIcon fontSize="small" />
          </IconButton>
        </Stack>
      </AccordionSummary>
      <AccordionDetails>
        <Stack spacing={2}>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
            <TextField
              label="Title"
              value={meal.title ?? ''}
              onChange={(e) => onChange({ title: e.target.value })}
              size="small"
              sx={{ flex: 1 }}
            />
            <TextField
              label="Time"
              type="time"
              value={meal.suggestedTime ?? ''}
              onChange={(e) => onChange({ suggestedTime: e.target.value })}
              size="small"
              InputLabelProps={{ shrink: true }}
            />
            <TextField
              label="Approx. kcal"
              type="number"
              value={meal.approxCalories ?? ''}
              onChange={(e) =>
                onChange({ approxCalories: e.target.value === '' ? null : Number(e.target.value) })
              }
              size="small"
            />
          </Stack>
          <TextField
            label="Macros"
            placeholder="e.g. Carb 50g | Pro 18g | Fat 12g"
            value={meal.macros ?? ''}
            onChange={(e) => onChange({ macros: e.target.value })}
            size="small"
          />
          <TextField
            label="Slot notes"
            value={meal.notes ?? ''}
            onChange={(e) => onChange({ notes: e.target.value })}
            size="small"
            multiline
            maxRows={3}
          />

          <Divider textAlign="left" sx={{ my: 0.5 }}>
            Options
          </Divider>

          {options.map((option, optIdx) => (
            <OptionEditor
              key={`opt-${optIdx}`}
              option={option}
              optIdx={optIdx}
              units={units}
              onChange={(patch) =>
                updateOptions((prev) =>
                  prev.map((o, i) => (i === optIdx ? { ...o, ...patch } : o)),
                )
              }
              onRemove={() => updateOptions((prev) => prev.filter((_, i) => i !== optIdx))}
            />
          ))}

          <Button
            size="small"
            startIcon={<AddIcon />}
            onClick={() => updateOptions((prev) => [...prev, blankOption(prev.length)])}
            sx={{ alignSelf: 'flex-start' }}
          >
            Add option
          </Button>
        </Stack>
      </AccordionDetails>
    </Accordion>
  )
}

function OptionEditor({ option, optIdx, units, onChange, onRemove }) {
  const items = option.items ?? []
  const updateItems = (updater) => {
    const next = typeof updater === 'function' ? updater(items) : updater
    onChange({ items: next })
  }
  return (
    <Box sx={{ p: 2, border: '1px solid rgba(0,0,0,0.1)', borderRadius: 1.5, background: '#fafafa' }}>
      <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
        <Typography variant="subtitle2" sx={{ flex: 1 }}>
          Option {optIdx + 1}
        </Typography>
        <IconButton size="small" onClick={onRemove} aria-label="Remove option">
          <DeleteOutlineIcon fontSize="small" />
        </IconButton>
      </Stack>

      <Stack spacing={1.5}>
        <TextField
          label="Display name"
          value={option.displayName ?? ''}
          onChange={(e) => onChange({ displayName: e.target.value })}
          size="small"
        />
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
          <TextField
            label="Approx. kcal"
            type="number"
            value={option.approxCalories ?? ''}
            onChange={(e) =>
              onChange({ approxCalories: e.target.value === '' ? null : Number(e.target.value) })
            }
            size="small"
            sx={{ minWidth: 140 }}
          />
          <TextField
            label="Macros"
            value={option.macros ?? ''}
            onChange={(e) => onChange({ macros: e.target.value })}
            size="small"
            sx={{ flex: 1 }}
          />
        </Stack>
        <TextField
          label="Preparation notes"
          value={option.preparationNotes ?? ''}
          onChange={(e) => onChange({ preparationNotes: e.target.value })}
          size="small"
          multiline
          maxRows={3}
        />

        <Box>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
            Items
          </Typography>
          <Stack spacing={1}>
            {items.map((item, itemIdx) => (
              <ItemRow
                key={`item-${itemIdx}`}
                item={item}
                units={units}
                onChange={(patch) =>
                  updateItems((prev) =>
                    prev.map((it, i) => (i === itemIdx ? { ...it, ...patch } : it)),
                  )
                }
                onRemove={() => updateItems((prev) => prev.filter((_, i) => i !== itemIdx))}
              />
            ))}
            <Button
              size="small"
              startIcon={<AddIcon />}
              onClick={() => updateItems((prev) => [...prev, blankItem()])}
              sx={{ alignSelf: 'flex-start' }}
            >
              Add item
            </Button>
          </Stack>
        </Box>
      </Stack>
    </Box>
  )
}

function ItemRow({ item, units, onChange, onRemove }) {
  return (
    <Box
      sx={{
        display: 'grid',
        gridTemplateColumns: { xs: '1fr', md: '2.4fr 0.8fr 0.8fr 0.8fr 0.4fr' },
        gap: 1,
        alignItems: 'center',
      }}
    >
      <TextField
        label="Item"
        value={item.displayName ?? ''}
        onChange={(e) => onChange({ displayName: e.target.value })}
        size="small"
      />
      <TextField
        label="Amount"
        type="number"
        value={item.quantity?.amount ?? ''}
        onChange={(e) =>
          onChange({
            quantity: {
              ...(item.quantity ?? {}),
              amount: e.target.value === '' ? null : Number(e.target.value),
            },
          })
        }
        size="small"
      />
      <TextField
        select
        label="Unit"
        value={item.quantity?.unit ?? ''}
        onChange={(e) =>
          onChange({
            quantity: { ...(item.quantity ?? {}), unit: e.target.value },
          })
        }
        size="small"
      >
        <MenuItem value="">—</MenuItem>
        {units.map((u) => (
          <MenuItem key={u.code} value={u.code}>
            {u.label} ({u.code})
          </MenuItem>
        ))}
      </TextField>
      <TextField
        label="kcal"
        type="number"
        value={item.approxCalories ?? ''}
        onChange={(e) =>
          onChange({ approxCalories: e.target.value === '' ? null : Number(e.target.value) })
        }
        size="small"
      />
      <IconButton size="small" onClick={onRemove} aria-label="Remove item">
        <DeleteOutlineIcon fontSize="small" />
      </IconButton>
    </Box>
  )
}

function GuidelinesSection({ draft, update }) {
  return (
    <Box>
      <SectionTitle>Guidance lists</SectionTitle>
      <Stack spacing={2} sx={{ mt: 1 }}>
        <StringListEditor
          label="General guidelines"
          value={draft.generalGuidelines ?? []}
          onChange={(next) => update({ generalGuidelines: next })}
        />
        <StringListEditor
          label="Foods to favour"
          value={draft.foodsToFavor ?? []}
          onChange={(next) => update({ foodsToFavor: next })}
        />
        <StringListEditor
          label="Foods to avoid"
          value={draft.foodsToAvoid ?? []}
          onChange={(next) => update({ foodsToAvoid: next })}
        />
      </Stack>
    </Box>
  )
}

function StringListEditor({ label, value, onChange }) {
  return (
    <Box>
      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
        {label}
      </Typography>
      <Stack spacing={1}>
        {value.map((line, idx) => (
          <Stack key={`line-${idx}`} direction="row" spacing={1} alignItems="center">
            <TextField
              value={line}
              onChange={(e) => {
                const next = [...value]
                next[idx] = e.target.value
                onChange(next)
              }}
              size="small"
              fullWidth
              multiline
              maxRows={3}
            />
            <IconButton
              size="small"
              onClick={() => {
                const next = [...value]
                next.splice(idx, 1)
                onChange(next)
              }}
              aria-label={`Remove ${label} entry`}
            >
              <DeleteOutlineIcon fontSize="small" />
            </IconButton>
          </Stack>
        ))}
        <Button
          size="small"
          startIcon={<AddIcon />}
          onClick={() => onChange([...(value ?? []), ''])}
          sx={{ alignSelf: 'flex-start' }}
        >
          Add line
        </Button>
      </Stack>
    </Box>
  )
}

function FreeFormChips({ label, values, onChange }) {
  const [pending, setPending] = useState('')
  const commit = () => {
    const trimmed = pending.trim()
    if (!trimmed) return
    onChange([...(values ?? []), trimmed])
    setPending('')
  }
  return (
    <Box sx={{ flex: 1 }}>
      <TextField
        label={label}
        value={pending}
        onChange={(e) => setPending(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === 'Enter') {
            e.preventDefault()
            commit()
          }
        }}
        size="small"
        fullWidth
        helperText="Press Enter to add"
      />
      <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap" sx={{ mt: 1 }}>
        {(values ?? []).map((v, i) => (
          <Chip
            key={`${v}-${i}`}
            label={v}
            size="small"
            onDelete={() => onChange(values.filter((_, idx) => idx !== i))}
          />
        ))}
      </Stack>
    </Box>
  )
}

function SectionTitle({ children }) {
  return (
    <Typography variant="subtitle2" sx={{ fontWeight: 700, color: '#1f2937', textTransform: 'uppercase', letterSpacing: 0.5 }}>
      {children}
    </Typography>
  )
}

// ---------- Helpers ----------

/** Snapshots a server template into the editor's draft shape — note the Quantity preservation. */
function planFromTemplate(template, language) {
  return {
    dietPlanTemplateId: template.id,
    dietPlanTemplateCode: template.code,
    dietPlanTemplateVersion: template.version,
    language,
    title: template.title,
    description: template.description,
    approxCaloriesPerDay: template.approxCaloriesPerDay,
    approxMacroSplit: template.approxMacroSplit,
    meals: deepCopyMeals(template.meals ?? []),
    generalGuidelines: [...(template.generalGuidelines ?? [])],
    foodsToFavor: [...(template.foodsToFavor ?? [])],
    foodsToAvoid: [...(template.foodsToAvoid ?? [])],
    durationDays: 14,
    reviewAfter: '2 weeks',
    practitionerNotes: '',
    patientConditionContext: '',
    dietaryPreference: '',
    allergies: [],
  }
}

function planFromExisting(plan) {
  return {
    ...plan,
    meals: deepCopyMeals(plan.meals ?? []),
    generalGuidelines: [...(plan.generalGuidelines ?? [])],
    foodsToFavor: [...(plan.foodsToFavor ?? [])],
    foodsToAvoid: [...(plan.foodsToAvoid ?? [])],
    allergies: [...(plan.allergies ?? [])],
  }
}

function deepCopyMeals(meals) {
  return meals.map((meal) => ({
    ...meal,
    options: (meal.options ?? []).map((opt) => ({
      ...opt,
      items: (opt.items ?? []).map((it) => ({
        ...it,
        quantity: it.quantity ? { ...it.quantity } : null,
      })),
    })),
  }))
}

function blankSlot(currentLength) {
  return {
    mealSlot: 'custom',
    title: 'New meal slot',
    suggestedTime: '',
    sort: currentLength + 1,
    options: [],
  }
}

function blankOption() {
  return {
    code: '',
    displayName: '',
    items: [],
  }
}

function blankItem() {
  return {
    code: '',
    displayName: '',
    quantity: { amount: null, unit: '' },
  }
}

